import { useAppUpdater } from '@/application/container';
import { useAppUpdateStore } from '@/application/update/appUpdateStore';
import { useApplyUpdate } from '@/application/update/useAppUpdate';
import { env } from '@/shared/config/env';
import { formatVersion } from '@/shared/format/version';
import { strings } from '@/shared/strings';
import { Button } from '@/ui/primitives/Button';
import { toast } from '@/ui/primitives/toastStore';
import styles from './UpdateBanner.module.css';

/**
 * "A new version is available", with the one button that loads it.
 *
 * A strip above the content, like the offline strip, rather than a card over
 * it: on a phone the bottom of the screen is the job's action bar, and nothing
 * a technician was about to tap should move or be covered. It appears once per
 * new build and stays until Update is tapped; the five minute check never makes
 * it blink or come back.
 *
 * `floating` pins it to the top of the viewport instead, for the sign in screen,
 * which is a fixed layer of its own with no shell around it.
 *
 * It names the version on offer, "Ceramica v1.1.0 (12 Oct 2026) is available.",
 * in the same words the sign in screen and the profile will show once it is
 * loaded, whenever the server says which release it has and that is not the
 * one on this device. The same release built again, the service worker's
 * signal alone and a server that names no release keep the plain sentence. The
 * request of 5 October 2026 (third), section 8.
 */
export function UpdateBanner({ floating = false }: { floating?: boolean }) {
  const offered = useAppUpdateStore((state) => state.offered);
  const offeredRelease = useAppUpdateStore((state) => state.offeredRelease);
  const applying = useAppUpdateStore((state) => state.applying);
  const apply = useApplyUpdate(useAppUpdater());

  if (offered === null) return null;

  const message =
    offeredRelease !== null && offeredRelease.release !== env.appRelease
      ? strings.update.messageFor(formatVersion(offeredRelease.release, offeredRelease.releasedOn ?? ''))
      : strings.update.message;

  return (
    <div className={`${styles.bar} ${floating ? styles.floating : ''}`} role="status">
      <span className={styles.text}>{message}</span>
      <Button
        variant="primary"
        className={styles.action}
        disabled={applying}
        aria-busy={applying}
        onClick={() => {
          void apply().then((reloading) => {
            if (!reloading) toast(strings.update.offline);
          });
        }}
      >
        {applying ? strings.update.applying : strings.update.action}
      </Button>
    </div>
  );
}
